import { Button, Col, Form, Input, Row, Select } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AccountAsignationData } from "../../../models/AccountAsignationData";
import { CheckIsValidCommissionConfigurationName } from "../../../api/repositoryEP";
import { Contract } from "../../../models/Contract";
import { useRef } from "react";

interface AccountAsignationFormProps {
    selectedConfiguration: AccountAsignationData | null;
    setSelectedConfiguration: (aad: AccountAsignationData | null) => void;
    create: (name: string, contractSelected: string) => Promise<void>;
    editName: (id: string, name: string) => Promise<void>;
    isLoading: boolean;
    allContracts: Contract[];
    isCreating: boolean;
}

const AccountAsignationForm = (props: AccountAsignationFormProps): JSX.Element => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const validationIdRef = useRef(0);

    const [isValidatingName, setIsValidatingName] = useState(false);
    const [isValidName, setIsValidName] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const onFinish = async (values: { name: string, contract: string }) => {
        setSubmitting(true);
        try {
            if (props.selectedConfiguration) {
                await props.editName(props.selectedConfiguration.id, values.name);
            } else {
                await props.create(values.name, values.contract);
            }
        } finally {
            form.setFieldValue("name", ""); // Restablecer el campo después de finalizar
            setSubmitting(false);
        }
    };

    useEffect(() => {
        if (props.selectedConfiguration) {
            form.setFieldValue("name", props.selectedConfiguration.name);
            setHasChanges(false);
        } else {
            form.resetFields();
            setHasChanges(false);
        }
    }, [props.selectedConfiguration]);

    const [hasChanges, setHasChanges] = useState(false);


    useEffect(() => {
        if (props.selectedConfiguration) {
            form.setFieldValue("name", props.selectedConfiguration.name);
        } else {
            form.resetFields();
        }
    }, [props.selectedConfiguration, form]);

    return (
        <Form
            layout="vertical"
            onFinish={onFinish}
            form={form}
            initialValues={{ name: "" }}
            onValuesChange={(changedValues, allValues) => {
                if (changedValues.name !== undefined) {
                    setIsValidatingName(true);

                    if (props.selectedConfiguration) {
                        const original = props.selectedConfiguration.name.trim();
                        const current = (allValues.name || "").trim();
                        setHasChanges(original !== current);
                    } else {
                        setHasChanges(true);
                    }
                }
            }}
        >
            <Row gutter={[16, 16]}>
                <Col xs={24}>
                    <Form.Item
                        label={t("pages.account-asignation.name")}
                        name="name"
                        validateTrigger="onBlur"
                        rules={[
                            { required: true, message: t("pages.account-asignation.name-required") || "" },
                            {
                                validator: async (_, value) => {
                                    setIsValidatingName(true)
                                    if (!value || !value.trim()) {
                                        setIsValidatingName(false);
                                        setIsValidatingName(false)
                                        setIsValidName(false);
                                        return Promise.resolve();
                                    }

                                    if (
                                        props.selectedConfiguration &&
                                        props.selectedConfiguration.name.trim() === value.trim()
                                    ) {
                                        setIsValidatingName(false);
                                        setIsValidName(true);
                                        return Promise.resolve();
                                    }

                                    const currentValidationId = ++validationIdRef.current;

                                    try {
                                        const response = await CheckIsValidCommissionConfigurationName(value);
                                        const isValid = response?.type === 1 ? response.data : false;

                                        //  Ignorar si no es la última validación
                                        if (currentValidationId !== validationIdRef.current) {
                                            return Promise.resolve();
                                        }

                                        setIsValidName(isValid);

                                        if (!isValid) {
                                            return Promise.reject(
                                                new Error(t("pages.account-asignation.name-unavailable") || "")
                                            );
                                        }

                                        return Promise.resolve();

                                    } finally {
                                        if (currentValidationId === validationIdRef.current) {
                                            setIsValidatingName(false);
                                        }
                                    }
                                }
                            }
                        ]}
                    >
                        <Input />
                    </Form.Item>
                </Col>
                {props.isCreating && (
                    <Col xs={24}>


                        <Form.Item
                            name="contract"
                            label={t("pages.account-asignation.contract")}
                            rules={[
                                { required: true, message: t("pages.account-asignation.contract-required") || "" },
                            ]}>
                            <Select
                                className="app-select"
                                options={props.allContracts
                                    .filter((contract) => !contract.isBase)
                                    .map((contract) => ({
                                        label: contract.name,
                                        value: contract.id,
                                    }))}
                            />



                        </Form.Item>


                    </Col>
                )}
                <Col xs={24} md={24} style={{ textAlign: "right" }}>
                    <Button
                        loading={submitting}
                        className="app-button"
                        disabled={
                            isValidatingName ||
                            submitting ||
                            !isValidName ||
                            form.getFieldsError().some(({ errors }) => errors.length) ||
                            (!!props.selectedConfiguration && !hasChanges)
                        }
                        htmlType="submit"
                    >
                        {props.selectedConfiguration ? t("button.edit") : t("button.create")}
                    </Button>
                </Col>
            </Row>
        </Form>
    );
};

export default AccountAsignationForm;
